import {
  Envelope,
  HandPalm,
  type Icon,
  Info,
  Lock,
  UserCheck,
  UserCircleDashed,
  UserFocus,
  UserMinus,
  UsersThree,
} from "@phosphor-icons/react";

import IconAvatar from "common/components/IconAvatar";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useCampaignContext } from "../../../../context/CampaignContext";

import type { AudienceStats } from "../../../../types";

interface DetailItemProps {
  icon: Icon;
  label: string;
  value: number;
  infoText: string;
}

interface AudienceDetailsProps {
  audienceStats: AudienceStats;
}

export default function AudienceDetails({
  audienceStats,
}: AudienceDetailsProps) {
  const { outreach_type: outreachType } = useCampaignContext();
  const isDrip = outreachType === "drip";
  const isDesktop = useTwBreakpoint("lg");

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  const {
    valid,
    inmailOptimization,
    locked,
    blocked,
    headless,
    alreadyConnected,
    notLead,
    closed,
  } = audienceStats ?? {};

  const totalUsersFound = audienceStats
    ? valid + locked + blocked + headless + alreadyConnected + notLead
    : 0;

  const detailItems: DetailItemProps[] = [
    {
      icon: UserCheck,
      label: "Valid targets",
      value: valid,
      infoText:
        "The number of targets that will receive communication from this campaign",
    },
    ...(isDrip
      ? [
          {
            icon: UserFocus,
            label: "Not a lead",
            value: notLead,
            infoText:
              "Inbound campaigns can only message Leads, which include your first connections, and profiles you have an ongoing conversation with",
          },
        ]
      : [
          {
            icon: Envelope,
            label: "Open profiles (InMail)",
            value: inmailOptimization,
            infoText:
              "The number of Valid targets that can receive a free InMail. These are Premium LinkedIn accounts with an open profile",
          },
          {
            icon: HandPalm,
            label: "Closed profiles (InMail)",
            value: closed,
            infoText: "Closed accounts do not accept free InMails",
          },
          {
            icon: UsersThree,
            label: "Already connected",
            value: alreadyConnected,
            infoText:
              "You are already connected with these targets. 1st connections cannot be enrolled in a campaign",
          },
        ]),
    {
      icon: Lock,
      label: "Locked-already in campaign",
      value: locked,
      infoText:
        'These targets are already enrolled and "locked" in a different campaign, to avoid accidentally spamming them. To override this and target these leads, enable the "Previously targeted" exclude setting during campaign creation',
    },
    {
      icon: UserMinus,
      label: "Blacklisted",
      value: blocked,
      infoText:
        "These accounts have been blacklisted, and will never be added to your campaigns",
    },
    {
      icon: UserCircleDashed,
      label: "Out of network",
      value: headless,
      infoText:
        "These LinkedIn accounts are out of your network and you cannot engage with them",
    },
  ];

  return (
    <Component>
      <Trigger asChild>
        <Button
          variant="outline-black"
          leftIcon={<Info weight="fill" />}
          size={isDesktop ? "sm" : "md"}
        >
          <span className="truncate">View details</span>
        </Button>
      </Trigger>

      <Content>
        <Header
          title="Audience details"
          subtitle="You will not engage with locked, blacklisted, and out of network accounts"
        />

        <Body>
          {detailItems.map(({ icon: Icon, infoText, label, value }) => (
            <article
              key={label}
              className="flex w-full items-center border-b border-b-black-200 py-3"
            >
              <IconAvatar size="sm">
                <Icon />
              </IconAvatar>

              <span className="ml-2 mr-1 text-button-md">{label}</span>

              <Tooltip>
                <TooltipTrigger>
                  <Info weight="fill" size={20} className="fill-black-300" />
                </TooltipTrigger>

                <TooltipContent>{infoText}</TooltipContent>
              </Tooltip>

              <span className="ml-auto text-button-sm">{value}</span>
            </article>
          ))}

          <div className="mt-3 flex justify-end gap-1">
            <p className="text-button-sm">
              Total users found: {totalUsersFound}
            </p>

            <Tooltip>
              <TooltipTrigger>
                <Info weight="fill" size={20} className="fill-black-300" />
              </TooltipTrigger>

              <TooltipContent>
                The number of targets found for this campaign. Sometimes it is
                possible for LinkedIn not to return the exact number of targets
                you requested when setting up your search. A 5-10% drop is
                normal.
              </TooltipContent>
            </Tooltip>
          </div>

          <RenderIf condition={isDesktop}>
            <DialogClose asChild>
              <Button className="mt-8 self-end" variant="secondary-black">
                Close
              </Button>
            </DialogClose>
          </RenderIf>
        </Body>
      </Content>
    </Component>
  );
}
